/**
 * CommandBarAskPanel — renders the agentic AI response.
 *
 * Composition (top to bottom):
 *   1. Answer card      — the 1–3 sentence reply from the LLM
 *   2. Action chips     — mutation summaries (created/updated/marked
 *                         done) with future-Undo affordance
 *   3. Result cards     — clickable item refs, deep-link to the right
 *                         page on click
 *
 * Empty state: while loading, shows a spinner with "Thinking…".
 * On error, shows the error inline so the user can retry.
 */
import { Sparkles, ArrowUpRight, ListChecks, Phone, ReceiptText, Target, BookOpen, MessageSquare, Bot, AlertCircle, HelpCircle, Check, Clock3, Trash2 } from 'lucide-react';
import { formatDate } from '../../utils/locale';
import { formatMoneyMinor } from '../../utils/money';
const TYPE_ICON = {
  task: ListChecks,
  call: Phone,
  payment: ReceiptText,
  commitment: Target,
  reading: BookOpen,
  note: MessageSquare,
};

// Per-action icon shown in the proposal chip — small visual cue
// that complements the "Did you mean…?" wording so users see at a
// glance what would happen on click.
const ACTION_ICON = {
  mark_done: Check,
  snooze: Clock3,
  delete: Trash2,
};

const ACTION_VERB = {
  mark_done: 'Mark done',
  snooze: 'Snooze',
  delete: 'Delete',
};


/**
 * StreamingSteps — vertical list of per-step progress labels.
 *
 * Shown during loading instead of a single opaque spinner. Each step
 * is either `active` (the agent is doing it RIGHT NOW — small
 * spinner) or `done` (the agent moved past it — check mark). The
 * "Drafting your answer…" beat is the final step before the answer
 * card replaces this list entirely.
 *
 * Empty-state during loading (no steps yet — the first event hasn't
 * landed) falls back to the classic "Thinking…" line so the user
 * never sees a blank panel.
 */
function StreamingSteps({ steps }) {
  if (!steps || steps.length === 0) {
    return (
      <div data-testid="commandbar-ai-loading" className="px-5 py-4 flex items-center gap-3">
        <div
          className="w-5 h-5 rounded-full animate-spin"
          style={{ border: '2px solid var(--pd-border)', borderTopColor: 'var(--pd-focus)' }}
        />
        <span className="text-sm" style={{ color: 'var(--pd-muted)' }}>
          Thinking…
        </span>
      </div>
    );
  }
  return (
    <div data-testid="commandbar-ai-steps" className="px-5 py-4 space-y-1.5">
      {steps.map((s, i) => (
        <div
          key={`${s.step}-${i}`}
          data-testid={`commandbar-ai-step-${i}`}
          data-status={s.status}
          className="flex items-center gap-2.5 text-sm transition-opacity"
          style={{
            color: s.status === 'active' ? 'var(--pd-text)' : 'var(--pd-muted)',
            opacity: s.status === 'done' ? 0.65 : 1,
          }}
        >
          {s.status === 'active' ? (
            <div
              className="w-3 h-3 rounded-full animate-spin flex-shrink-0"
              style={{ border: '2px solid var(--pd-border)', borderTopColor: 'var(--pd-focus)' }}
            />
          ) : (
            <Check
              className="w-3 h-3 flex-shrink-0"
              style={{ color: 'var(--pd-focus)' }}
            />
          )}
          <span className="truncate">{s.label}</span>
          {/* The "Found N" beat — a faint pill that tucks against the
              step's main label once the backend's `result` event has
              told us how many items the tool returned. Only renders
              on completed steps with a non-empty summary; the active
              step never has one yet. */}
          {s.status === 'done' && s.summary ? (
            <span
              data-testid={`commandbar-ai-step-${i}-summary`}
              className="text-[10px] font-mono tabular-nums uppercase tracking-wider rounded-full px-1.5 py-0.5 flex-shrink-0"
              style={{
                background: 'var(--pd-panel-2)',
                color: 'var(--pd-subtle)',
                border: '1px solid var(--pd-border)',
              }}
            >
              {s.summary}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}


function ResultCard({ item, onSelect }) {
  const Icon = TYPE_ICON[item.type] || Bot;
  const moneyLabel = item.type === 'payment' && item.amountMinor != null
    ? formatMoneyMinor(item.amountMinor, item.currency)
    : null;
  return (
    <button
      data-testid={`commandbar-ai-result-${item.id}`}
      onClick={() => onSelect(item)}
      className="w-full flex items-start gap-3 px-5 py-3 text-left transition-colors duration-150"
      style={{ background: 'transparent' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--pd-panel-2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--pd-muted)' }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[9px] uppercase tracking-[0.10em] font-semibold" style={{ color: 'var(--pd-subtle)' }}>
            {item.type}
          </span>
          {item.businessShortCode ? (
            <span
              className="text-[9px] font-mono uppercase tracking-[0.06em] rounded-full px-1.5"
              style={{
                background: item.businessColor ? `${item.businessColor}24` : 'var(--pd-panel-2)',
                color: item.businessColor || 'var(--pd-muted)',
              }}
            >
              {item.businessShortCode}
            </span>
          ) : null}
          {item.priority ? (
            <span className="text-[9px] uppercase tracking-[0.10em] font-semibold" style={{ color: 'var(--pd-muted)' }}>
              {item.priority}
            </span>
          ) : null}
        </div>
        <div className="text-sm mt-1 truncate" style={{ color: 'var(--pd-text)', fontWeight: 500 }}>
          {item.title || '(untitled)'}
        </div>
        {(moneyLabel || item.subtitle || item.dueDate) ? (
          <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--pd-subtle)' }}>
            {moneyLabel || item.subtitle}
            {item.dueDate ? ` · due ${formatDate(item.dueDate)}` : ''}
          </div>
        ) : null}
      </div>
      <ArrowUpRight className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: 'var(--pd-subtle)' }} />
    </button>
  );
}


export function CommandBarAskPanel({ loading, answer, results, actions, proposals, error, locked, steps, onSelectResult, onConfirmProposal, onConfirmAll }) {
  if (loading && !answer) {
    return <StreamingSteps steps={steps} />;
  }

  if (error) {
    return (
      <div data-testid="commandbar-ai-error" className="px-5 py-4 flex items-start gap-3">
        <AlertCircle className="w-4 h-4 mt-0.5" style={{ color: 'var(--pd-warning)' }} />
        <div className="text-sm" style={{ color: 'var(--pd-text)' }}>{error}</div>
      </div>
    );
  }

  const hasProposals = (proposals || []).length > 0;
  if (!answer && results.length === 0 && (actions || []).length === 0 && !hasProposals) {
    return null;
  }

  return (
    <div data-testid="commandbar-ai-panel" className="border-b" style={{ borderColor: 'var(--pd-border)' }}>
      {/* Answer card */}
      {answer ? (
        <div
          data-testid="commandbar-ai-answer"
          className="px-5 py-4 flex items-start gap-3"
          style={{ borderLeft: '3px solid var(--pd-focus)', background: 'var(--pd-panel-2)' }}
        >
          <Sparkles className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: 'var(--pd-focus)' }} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.10em] font-semibold mb-1.5" style={{ color: 'var(--pd-subtle)' }}>
              PolkDesQ AI
            </div>
            <div className="text-sm leading-relaxed" style={{ color: 'var(--pd-text)' }}>
              {answer}
            </div>
          </div>
        </div>
      ) : null}

      {/* Disambiguation proposals — clickable chips. Each chip carries
          enough payload (kind + id + action) for the deterministic
          /api/ai/confirm endpoint, so clicking is instant — no LLM
          round-trip. The "Did you mean…?" header reads naturally
          alongside the answer card above.

          When all proposals share the same action (e.g. all
          `mark_done`), a leading "Apply to all N" chip lets the user
          batch-confirm in one click via /api/ai/confirm-batch. */}
      {hasProposals ? (
        <div
          data-testid="commandbar-ai-proposals"
          className="px-5 py-3 space-y-2"
          style={{ background: 'var(--pd-panel)', borderTop: '1px solid var(--pd-border)' }}
        >
          <div className="flex items-center gap-1.5">
            <HelpCircle className="w-3.5 h-3.5" style={{ color: 'var(--pd-warning)' }} />
            <div className="text-[10px] uppercase tracking-[0.10em] font-semibold" style={{ color: 'var(--pd-subtle)' }}>
              Did you mean…?
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* "Apply to all N" — only when 2+ proposals share the
                same action. The chip is visually distinct (filled
                background, no per-item title) so it reads as a
                shortcut rather than a sibling option. */}
            {proposals.length > 1 && proposals.every((p) => p.action === proposals[0].action) ? (
              <button
                data-testid="commandbar-ai-proposal-batch"
                onClick={() => onConfirmAll?.()}
                className="pd-btn-press inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors"
                style={{
                  background: 'var(--pd-focus)',
                  color: 'white',
                  border: '1px solid var(--pd-focus)',
                }}
                title={`${ACTION_VERB[proposals[0].action] || proposals[0].action} all ${proposals.length}`}
              >
                <Sparkles className="w-3 h-3" />
                <span>Apply to all {proposals.length}</span>
              </button>
            ) : null}
            {proposals.map((p, i) => {
              const Icon = ACTION_ICON[p.action] || Bot;
              return (
                <button
                  key={`${p.kind}-${p.id}-${i}`}
                  data-testid={`commandbar-ai-proposal-${i}`}
                  onClick={() => onConfirmProposal?.(p)}
                  className="pd-btn-press inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors"
                  style={{
                    background: 'var(--pd-panel-2)',
                    border: '1px solid var(--pd-border)',
                    color: 'var(--pd-text)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--pd-focus)'; e.currentTarget.style.color = 'white'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--pd-panel-2)'; e.currentTarget.style.color = 'var(--pd-text)'; }}
                  title={`${ACTION_VERB[p.action] || p.action}: ${p.title}`}
                >
                  <Icon className="w-3 h-3" />
                  <span className="truncate max-w-[280px]">{ACTION_VERB[p.action] || p.action} · {p.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Mutation chips */}
      {(actions || []).length > 0 ? (
        <div data-testid="commandbar-ai-actions" className="px-5 py-2 flex flex-wrap gap-2 items-center" style={{ background: 'var(--pd-panel)' }}>
          {actions.map((a, i) => (
            <span
              key={`${a.kind || 'action'}-${a.id || i}`}
              data-testid={`commandbar-ai-action-${i}`}
              className="text-[11px] rounded-full px-2.5 py-1 inline-flex items-center gap-1.5 font-semibold"
              style={{
                background: 'var(--pd-success-soft, var(--pd-panel-2))',
                color: 'var(--pd-success, var(--pd-text))',
                border: '1px solid var(--pd-border)',
              }}
            >
              ✓ {a.label || `${a.kind} updated`}
            </span>
          ))}
          {/* After any mutation, the input is locked from auto-firing
              again until the user clears it. We hint this inline so
              the user understands why typing more won't trigger a new
              create — prevents the "I added more detail and got a
              duplicate" foot-gun. */}
          {locked ? (
            <span
              data-testid="commandbar-ai-locked-hint"
              className="text-[10px] uppercase tracking-[0.10em] font-semibold"
              style={{ color: 'var(--pd-subtle)' }}
            >
              · Clear input to ask again
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Result cards */}
      {results.length > 0 ? (
        <div data-testid="commandbar-ai-results" className="py-1">
          {results.slice(0, 10).map((item) => (
            <ResultCard key={item.id || item.title} item={item} onSelect={onSelectResult} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
